/**
 * dashboardPersona - whose dashboard this is, and what it should show them
 * before they have ever touched it.
 *
 * A dashboard that ships one static grid to everyone is a dashboard that is
 * wrong for almost everyone: an in-house team lead opens it to a matters wall,
 * an individual client opens it to six cards about a firm they are not in, and
 * a managing partner has to go and FIND the economics that are the reason they
 * opened the page. The fix is not "make it empty and let them build it" —
 * an empty dashboard is a chore, and most people never do the chore. It is to
 * make the DEFAULT smart.
 *
 * THE PERSONA IS DERIVED FROM STABLE FACTS, deliberately:
 *
 *   • the organisation's TYPE (`user.userInfo.org_type`) - what kind of work
 *     this account does at all;
 *   • whether the user ADMINISTERS that organisation (`overview.is_org_admin`,
 *     which the summary endpoint already computes).
 *
 * It is NOT derived from live counters. A default that reads `matters > 0`
 * would re-order itself the week someone closes their last matter, and a
 * dashboard that rearranges on its own is worse than one that is slightly
 * generic. Live data still decides whether a widget RENDERS - that is the
 * registry's `requires` gate, which runs per paint - so the two work together:
 * the persona picks the arrangement, `requires` drops the cards that have
 * nothing to say today, and the rows flow up to close the gap.
 *
 * A STORED LAYOUT ALWAYS WINS. This only produces `grid.defaults`, which is
 * what a user sees on their first visit and what "Reset to default" restores.
 * The moment they arrange anything, `useWidgetLayout` persists it and the
 * persona stops being visible. Changing these lists is therefore safe: it
 * moves nobody who has customised their dashboard.
 */

import {
  WidgetGrid,
  WidgetLayout,
  WidgetSlot,
  SPAN_UNITS,
} from './widgetLayout';
import { DashboardWidgetId, WIDGETS, WIDGET_IDS } from './widgets/registry';

/** The kind of work an account does - the axis that orders the page. */
export type DashboardAudience = 'legal' | 'business' | 'individual';

export interface DashboardPersona {
  audience: DashboardAudience;
  /** Administers the organisation: gets the firm-wide cards as well. */
  isOrgAdmin: boolean;
}

/** What the persona is resolved FROM. Both fields may be absent at boot. */
export interface DashboardPersonaInput {
  /** `user.userInfo.org_type` - 'law_firm', 'business', 'individual_client', … */
  orgType?: string | null;
  /** `overview.is_org_admin` from the summary endpoint. */
  isOrgAdmin?: boolean | null;
}

const AUDIENCE_BY_ORG_TYPE: Record<string, DashboardAudience> = {
  law_firm: 'legal',
  solo_lawyer: 'legal',
  business: 'business',
  in_house_team: 'business',
  individual_client: 'individual',
};

/**
 * The persona for an account.
 *
 * Unknown or missing org types fall to `legal`, which is the arrangement the
 * dashboard has always shipped - an account we cannot classify keeps the
 * behaviour it had rather than getting an experiment.
 */
export const resolvePersona = ({
  orgType,
  isOrgAdmin,
}: DashboardPersonaInput): DashboardPersona => {
  const audience = AUDIENCE_BY_ORG_TYPE[String(orgType || '').trim()] ?? 'legal';
  return {
    audience,
    // An individual client has no organisation to administer, whatever a
    // stale flag says.
    isOrgAdmin: audience !== 'individual' && !!isOrgAdmin,
  };
};

/** Two personas are the same when both fields match - used to memoise a grid. */
export const personaKey = (persona: DashboardPersona): string =>
  `${persona.audience}${persona.isOrgAdmin ? ':admin' : ''}`;

/* ------------------------------------------------------------------ */
/*  The arrangements                                                   */
/* ------------------------------------------------------------------ */

/**
 * What each audience sees, in reading order. Everything a list omits is
 * REGISTERED BUT HIDDEN - it is in the gallery, one click from being added,
 * rather than absent.
 *
 * The shape of every list is the same argument: the top of the page answers
 * "where do I stand and what is on fire", the middle is the work itself in
 * whatever order that audience thinks about it, then the ways to START work,
 * and the bottom is context - what changed and what is coming.
 *
 * THE TOP ROW IS ONE READ: greeting (which now carries the "Start something"
 * tool tiles - they used to be a separate `tools` card under the work) ·
 * Needs you · At a glance. `processing` follows it: a transient "the AI is
 * still chewing on this" strip that belongs with orientation, not wedged into
 * the work. Read down the page: where you stand → the work → what happened.
 *
 * WHY `legal` AND `business` NOW HOLD THE SAME LIST. The axis that separated
 * them was which STANDALONE list came first - Recent Matters for a firm,
 * My Projects for an in-house team. Those three widgets were duplicates of the
 * three columns inside `urgentAttention` and have been removed, so there is
 * nothing left for the audience to reorder. The two entries are kept rather
 * than collapsed because the distinction is real and the next widget that
 * cares about it should not have to reintroduce the concept.
 */
const AUDIENCE_ORDER: Record<DashboardAudience, DashboardWidgetId[]> = {
  // A firm: orientation, the work card, then context.
  legal: [
    'greeting',
    'needsAttention',
    'quickMetrics',
    'processing',
    'urgentAttention',
    // The distinct things you last opened, directly under the work rather than
    // down in the chronology (UI feedback D-6).
    'recentlyWorkedOn',
    'activity',
    'upcomingEvents',
  ],
  // An in-house team. Same shape - see the note above on why.
  business: [
    'greeting',
    'needsAttention',
    'quickMetrics',
    'processing',
    'urgentAttention',
    'recentlyWorkedOn',
    'activity',
    'upcomingEvents',
  ],
  // One person, their own work. No firm-wide cards; the greeting drops the
  // tool tiles by licence if there is nothing they can reach.
  individual: [
    'greeting',
    'needsAttention',
    'quickMetrics',
    'urgentAttention',
    'recentlyWorkedOn',
    'upcomingEvents',
    'activity',
  ],
};

/** The card that carries the day's work - three columns, five rows each. */
export const WORK_WIDGET: DashboardWidgetId = 'urgentAttention';

/**
 * The two cards an administrator opens the page FOR, spliced into their
 * audience's order rather than bolted on the end: money goes with the other
 * numbers at the top, and the productivity strip sits with the other
 * retrospective cards near the bottom.
 */
const withAdminCards = (order: DashboardWidgetId[]): DashboardWidgetId[] => {
  const out = [...order];
  const after = (anchor: DashboardWidgetId, id: DashboardWidgetId) => {
    const at = out.indexOf(anchor);
    if (out.includes(id)) return;
    if (at === -1) out.push(id);
    else out.splice(at + 1, 0, id);
  };
  /*
    The two admin ECONOMICS cards sit together: Firm Economics then Productivity,
    both half a row wide, so they pair into one clean row instead of Firm
    Economics stranding alone with an empty half beside it. Moving Productivity
    up here also lets Activity pair with Upcoming Events in the row below, so no
    card is left half-width-and-alone. `productivity.maxSpan` still stops it
    swallowing the whole row if Firm Economics is ever hidden.
  */
  after('quickMetrics', 'firmEconomics');
  after('firmEconomics', 'productivity');
  return out;
};

/** The visible widgets for a persona, in order. */
export const personaOrder = (persona: DashboardPersona): DashboardWidgetId[] => {
  const base = AUDIENCE_ORDER[persona.audience] ?? AUDIENCE_ORDER.legal;
  return persona.isOrgAdmin ? withAdminCards(base) : [...base];
};

/**
 * A persona's full layout: its visible widgets packed into rows, then every
 * other registered widget appended hidden so the gallery can offer it.
 */
export const buildPersonaDefaults = (
  persona: DashboardPersona
): WidgetLayout<DashboardWidgetId> => {
  const visible = personaOrder(persona).filter((id) => !!WIDGETS[id]);
  const hidden = WIDGET_IDS.filter((id) => !visible.includes(id));
  const order = [...visible, ...hidden];

  const slots = {} as Record<DashboardWidgetId, WidgetSlot<DashboardWidgetId>>;
  let row = 0;
  let col = 0;
  order.forEach((id) => {
    const definition = WIDGETS[id];
    if (col + definition.defaultSpan > SPAN_UNITS && col > 0) {
      row += 1;
      col = 0;
    }
    slots[id] = {
      id,
      row,
      col,
      span: definition.defaultSpan,
      height: definition.defaultHeight,
      visible: visible.includes(id),
    };
    col += definition.defaultSpan;
  });

  return { slots, order };
};

/**
 * The grid for a persona.
 *
 * The STORAGE SCOPE is the same for every persona on purpose - a dashboard is
 * keyed by user, not by role. Someone promoted to org admin keeps the
 * arrangement they built; what changes is only what "Reset to default" gives
 * them back.
 */
export const buildDashboardGrid = (
  persona: DashboardPersona
): WidgetGrid<DashboardWidgetId> => ({
  ids: WIDGET_IDS,
  minSpan: WIDGET_IDS.reduce((acc, id) => {
    const min = WIDGETS[id].minSpan;
    if (min) acc[id] = min;
    return acc;
  }, {} as Partial<Record<DashboardWidgetId, WidgetSlot['span']>>),
  maxSpan: WIDGET_IDS.reduce((acc, id) => {
    const max = WIDGETS[id].maxSpan;
    if (max) acc[id] = max;
    return acc;
  }, {} as Partial<Record<DashboardWidgetId, WidgetSlot['span']>>),
  defaults: buildPersonaDefaults(persona),
  // No entityId: one dashboard per user, not one per record.
  scope: { kind: 'dashboard', name: 'home' },
});

export default buildDashboardGrid;
